import {from, Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import Feature from '@/interfaces/geo/feature.interface';
import MapObject, {MapObjectImage} from '@/interfaces/entities/map-object.interface';
import ApiService from '@/services/api.service';
import Filters from '@/library/filter/filters';
import FeatureUpdateAttributes from '@/interfaces/geo/feature-update-attributes';
import {IImageData} from '../../../../../media/com_mammadaba_map/map/geo/Map/imageProvider';
import MammadabaMap from '@/interfaces/geo/mammadaba-map.interface';
import MapRoute from '@/interfaces/entities/map-route.interface';
import {ForestGame} from '@/library/forest-game/forest-game';
import {MapObjectType} from '@/enums/map-object-type';

declare const GeoMammadabaMap: MammadabaMap;

export default class MapFeaturesService {
    private static instance: MapFeaturesService;
    private localAllFeatures: Feature[] = [];
    private localAllRouteLayers: Feature[][] = [];
    private localAllGameLayers: Feature[][] = [];
    private localImagesCache: { [objectId: string]: MapObjectImage[] } = {};
    private apiService: ApiService = ApiService.getInstance();

    private constructor() {
    }

    public static getInstance(): MapFeaturesService {
        if (!MapFeaturesService.instance) {
            MapFeaturesService.instance = new MapFeaturesService();
        }
        return MapFeaturesService.instance;
    }

    public isFeatureInFeaturesList(featureId: number | string): boolean {
        return this.localAllFeatures.some((feature: Feature) => feature.getId().toString() === featureId.toString());
    }

    public allFeatures(): Feature[] {
        return this.featuresWithUserStates(this.localAllFeatures)
    }

    public allRouteLayers(): Feature[][] {
        return this.routesWithUserStates(this.localAllRouteLayers);
    }

    public allGameLayers(): Feature[][] {
        return this.localAllGameLayers.map((features: Feature[]) => this.gameFeaturesWithUserStates(features));
    }

    public loadAllFeatures(): Observable<void> {
        return from(GeoMammadabaMap.getAllFeatures()).pipe(
            map((features: Feature[]) => {
                this.localAllFeatures = features;
            })
        );
    }

    public loadAllRouteLayers(): Observable<void> {
        return from(GeoMammadabaMap.getAllRoutes()).pipe(
            map((routeLayers: Feature[][]) => {
                this.localAllRouteLayers = routeLayers;
            })
        );
    }

    public loadAllGameLayers(): Observable<Feature[][]> {
        return from(GeoMammadabaMap.getAllGames()).pipe(
            map((gameLayers: Feature[][]) => {
                this.localAllGameLayers = gameLayers;
                return gameLayers;
            }),
            map((layers: Feature[][]) => {
                return layers.map((features: Feature[]) => this.gameFeaturesWithUserStates(features));
            })
        );
    }

    public objectFeatures(filters: Filters): Observable<Feature[]> {
        return of(GeoMammadabaMap.filterFeatures(this.allFeatures(), filters.filtersObject())).pipe(
            map((features: Feature[]) => {
                if (filters.searchFilter && filters.searchFilter.value) {
                    features = GeoMammadabaMap.filterByText(
                        features,
                        filters.searchFilter.value.toString()
                    );
                }
                return features;
            }),
            map((features: Feature[]) => this.featuresWithUserStates(features))
        );
    }

    public objectFeature(mapObjectId: string): Observable<Feature> {
        return this.objectFeatures(new Filters()).pipe(
            map((features: Feature[]) => {
                const foundFeature = features.find(feature => feature.getId().toString() === mapObjectId);
                if (foundFeature) {
                    return foundFeature;
                } else {
                    throw new Error('not_found');
                }
            })
        );
    }

    public routeFeatures(filters: Filters): Observable<Feature[]> {
        return of(GeoMammadabaMap.filterFeatures(this.allRouteLayers()[0], filters.filtersObject())).pipe(
            map((features: Feature[]) => {
                if (filters.searchFilter && filters.searchFilter.value) {
                    features = GeoMammadabaMap.filterByText(
                        features,
                        filters.searchFilter.value.toString()
                    );
                }
                return features;
            }),
            map((features: Feature[]) => this.featuresWithUserStates(features))
        );
    }

    public routeFeature(routeId: string): Observable<Feature> {
        return this.routeFeatures(new Filters()).pipe(
            map((features: Feature[]) => {
                const foundFeature = features.find(feature => feature.values_.ROUTEID.toString() === routeId);
                if (foundFeature) {
                    return foundFeature;
                } else {
                    throw new Error('not_found');
                }
            })
        );
    }

    public gameFeatureForGameId(gameId: string): Observable<Feature> {
        return of(null).pipe(
            map(() => {
                return this.allGameLayers().filter((features: Feature[]) => {
                    return features.some((feature: Feature) => feature.values_.GAMEPOINTTYPE === 'GameMainPoint')
                })[0];
            }),
            map((features: Feature[]) => {
                const foundFeature = features.find(feature => feature.values_.GAMEID.toString() === gameId);
                if (foundFeature) {
                    return foundFeature;
                } else {
                    throw new Error('not_found');
                }
            })
        );
    }

    public gameFeatureForGameMapPoint(pointId: string): Observable<Feature> {
        return of(null).pipe(
            map(() => {
                return this.allGameLayers().filter((features: Feature[]) => {
                    return features.some((feature: Feature) => feature.values_.GAMEPOINTTYPE === 'GameCheckPoint')
                })[0];
            }),
            map((features: Feature[]) => {
                const foundFeature = features.find(feature => feature.values_.POINTID.toString() === pointId);
                if (foundFeature) {
                    return foundFeature;
                } else {
                    throw new Error('not_found');
                }
            })
        );
    }

    public mapObjectFromObjectFeature(feature: Feature): MapObject {
        const id: string = feature.getId().toString();
        const features: string[] = (feature.values_.TOURISMTYPESSTR || '').split(';');
        return {
            id: id,
            type: MapObjectType.Object,
            title: feature.values_.PLACENAME,
            description: feature.values_.PLACEDESCRIPTION,
            mapFeature: feature,
            features: features
                .filter((id: string) => id !== '')
                .map((id: string) => {
                    return {
                        name: GeoMammadabaMap.tourismTypes.title[id],
                        icon: GeoMammadabaMap.tourismTypes.url + id + '.png'
                    };
                }),
            coordinates: feature.values_.WGS84 ? {
                lat: feature.values_.WGS84[1],
                long: feature.values_.WGS84[0]
            } : null,
            visited: feature.values_.VISITED === 1,
            myFavorite: feature.values_.LIKE === 1,
            managedByLvm: feature.values_.PLACEMANAGERNAME === 'Latvijas valsts meži',
            images: null,
        };
    }

    public mapObjectFromRouteFeature(feature: Feature): MapObject {
        const object: MapObject = this.mapObjectFromObjectFeature(feature);
        object.id = feature.values_.ROUTEID.toString();
        object.type = MapObjectType.Route;
        object.description = feature.values_.SHORTDESCRIPTION;
        return object;
    }

    public mapObjectImages(object: MapObject): Observable<MapObjectImage[]> {
        const objectId: string = object.mapFeature.getId().toString();
        return this.localImagesCache[objectId]
            ? of(this.localImagesCache[objectId])
            : from(GeoMammadabaMap.getRelatedImages(object.mapFeature.values_.OBJECTID, object.type === MapObjectType.Route)).pipe(
                map((images: IImageData[]) => {
                    const mapObjectImages: MapObjectImage[] = (images || []).map((image: IImageData) => {
                        return this.mapObjectImageFromImageData(image);
                    });
                    this.localImagesCache[objectId] = mapObjectImages;
                    return mapObjectImages;
                })
            );
    }

    public mapRouteFromFeature(feature: Feature): MapRoute {
        const id: string = feature.values_.ROUTEID.toString();
        return {
            id: id,
            title: feature.values_.PLACENAME,
            mapFeature: feature,
            myFavorite: feature.values_.LIKE === 1,
        };
    }

    public updateAttributes(feature: Feature, attributes: FeatureUpdateAttributes): void {
        GeoMammadabaMap.updateAttributes(feature, attributes, this.allFeatures());
    }

    private featuresWithUserStates(features: Feature[]): Feature[] {
        return features.map(feature => {
            feature = this.featureWithUserVisitedState(feature);
            feature = this.featureWithUserFavoriteState(feature);
            return feature;
        });
    }

    private featureWithUserVisitedState(feature: Feature): Feature {
        const hasVisited = this.apiService.visited().some((checkVisited: string | number) => {
            return checkVisited.toString() === feature.getId().toString();
        });
        feature.values_.VISITED = hasVisited ? 1 : 0;
        return feature;
    }

    private featureWithUserFavoriteState(feature: Feature): Feature {
        const hasFavorite = this.apiService.favorites().some(checkFavorite => {
            return checkFavorite.type === 'map-object' && checkFavorite.id.toString() === feature.getId().toString();
        });
        feature.values_.LIKE = hasFavorite ? 1 : 0;
        return feature;
    }

    private routesWithUserStates(features: Feature[][]): Feature[][] {
        return features;
    }

    private gameFeaturesWithUserStates(features: Feature[]): Feature[] {
        if (features.length > 0) {
            const firstFeature: Feature = features[0];
            const game: ForestGame = new ForestGame(this.apiService.forestGame());
            if (firstFeature.values_.GAMEPOINTTYPE === 'GameCheckPoint') {
                features = features.map((feature: Feature) => {
                    const isTaskCompleted = game.isMapCheckPointCompleted(feature);
                    feature.values_.GAMEPOINTSTATUS = isTaskCompleted ? 2 : 0;
                    return feature;
                });
            }
            if (firstFeature.values_.GAMEPOINTTYPE === 'GameMainPoint') {
                features = features.map((feature: Feature) => {
                    if (feature.values_.GAMEID.toString() === game.lvmGeoId() && game.isEnded()) {
                        feature.values_.GAMEPOINTSTATUS = 2;
                    }
                    return feature;
                });
            }
        }
        return features;
    }

    private mapObjectImageFromImageData(imageData: IImageData): MapObjectImage {
        const baseImageUrl = imageData.fullscreen;
        const thumbnailParams = ['width=195', 'height=130', 'fill=' + encodeURIComponent('#f7f7f7')];
        const originalParams = ['width=572', 'height=382', 'fill=' + encodeURIComponent('#f7f7f7')];
        return {
            title: imageData.title,
            description: imageData.description,
            fullscreen: imageData.fullscreen,
            thumbnail: baseImageUrl + '&' + thumbnailParams.join('&'),
            original: baseImageUrl + '&' + originalParams.join('&'),
        } as MapObjectImage;
    }
}
